import React, { useEffect, useState } from "react";
import { Button, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";

import {
  FormProvider,
  RHFRadioSingle,
  RHFSelect,
  RHFTextField,
} from "@app/components";
import { Strings } from "@app/constants";
import { useRequestState, useResponsive } from "@app/hooks";

import { budgetOptions, kmDrivenOptions } from "../../categoriesData";
import { BuyCarFormProps } from "../../types";
import { kmsDriven } from "@app/modules/product-list-page/filters-data";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { selectMyProfileState } from "@app/modules/my-profile/selector";

const BuyCarForm = ({
  brands,
  brandData,
  onFormValueChange,
  onClose,
}: BuyCarFormProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const navigate = useNavigate();

  const { data: profileData } = useRequestState({
    stateSelector: selectMyProfileState,
  });

  const methods = useForm({
    defaultValues: {
      searchType: "budget",
      budget: "",
      kmDriven: "",
      brand: "",
      model: "",
      name: profileData?.firstName || "",
      phone: profileData?.phone || "",
      email: profileData?.email || "",
    },
  });

  useEffect(() => {
    methods.reset({
      searchType: "budget",
      budget: "",
      kmDriven: "",
      brand: "",
      model: "",
      name: `${profileData?.firstName} ${profileData?.lastName}` || "",
      phone: profileData?.phone || "",
      email: profileData?.email || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const getFormsValues = methods.getValues();
  const allFormValues = methods.watch();

  const prevValuesRef = React.useRef(allFormValues);

  useEffect(() => {
    if (
      onFormValueChange &&
      JSON.stringify(prevValuesRef.current) !== JSON.stringify(allFormValues)
    ) {
      prevValuesRef.current = allFormValues;
      onFormValueChange(allFormValues);
    }
  }, [allFormValues, onFormValueChange]);

  const selectedBrand = methods.watch("brand");

  const filteredModels = selectedBrand
    ? brandData?.find((brands: any) => brands?.name === selectedBrand)
        ?.models || []
    : [];

  const dropdownStyle = {
    flex: 1,
    minWidth: isMobile ? "100%" : 240,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  };

  const handleBuyButton = () => {
    const currentValues = methods.getValues();
    navigate(PATH_DASHBOARD.productListPage, {
      state: {
        formParamsValues: currentValues,
      },
    });
    onClose();
  };

  const FormField = ({
    label,
    name,
    placeholder,
  }: {
    label: string;
    name: string;
    placeholder?: string;
  }) => (
    <Stack sx={{ flex: 1 }}>
      <Typography
        sx={{
          color: theme.palette.common.black,
          fontFamily: "Work Sans",
          fontSize: isMobile ? 12 : 13,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <RHFTextField
        disabled
        name={name}
        placeholder={placeholder}
        fullWidth
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.grey[200],
            borderRadius: 1.5,
            "& fieldset": { border: "none" },
          },
        }}
      />
    </Stack>
  );

  const renderDropdown = (
    name: string,
    label: string,
    options: { label: string; value: string }[],
    customMinWidth?: number,
  ) => (
    <RHFSelect
      name={name}
      placeholder={label}
      sx={{
        ...dropdownStyle,
        minWidth: customMinWidth || dropdownStyle.minWidth,
      }}
    >
      {options?.length > 0 ? (
        options.map((opt) => (
          <MenuItem key={opt?.value} value={opt?.value}>
            {opt?.label}
          </MenuItem>
        ))
      ) : (
        <Typography sx={{ textAlign: "center", fontFamily: "Work Sans" }}>
          {Strings.authPopup.pleaseSelectBrands}
        </Typography>
      )}
    </RHFSelect>
  );

  const searchType = methods.watch("searchType");

  return (
    <Stack sx={{ width: "100%", alignSelf: "center" }}>
      <FormProvider methods={methods}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={isMobile ? 2 : 3}
          mt={2}
          sx={{ width: "100%" }}
        >
          <FormField
            label={Strings.authPopup.name}
            name="name"
            placeholder="Name*"
          />
          <FormField
            label={Strings.authPopup.phoneNumber}
            name="phone"
            placeholder="Phone Number*"
          />
          <FormField
            label={Strings.authPopup.email}
            name="email"
            placeholder="Email"
          />
        </Stack>

        <Stack
          flexDirection="row"
          gap={2}
          color={theme.palette.common.black}
          sx={{ zIndex: 2, mt: 2 }}
        >
          <RHFRadioSingle name="searchType" label="By Budget" value="budget" />
          <RHFRadioSingle name="searchType" label="By Brand" value="brand" />
        </Stack>

        <Stack direction={isMobile ? "column" : "row"} spacing={2} mt={1}>
          {searchType === "budget" ? (
            <>
              {renderDropdown(
                "budget",
                Strings.authPopup.selectBudget,
                budgetOptions,
              )}
              {renderDropdown("kmDriven", Strings.filters.kmDriven, kmsDriven)}
            </>
          ) : (
            <>
              {renderDropdown(
                "brand",
                Strings.authPopup.selectBrand,
                brands?.map((b) => ({ label: b, value: b })),
                220,
              )}
              {renderDropdown(
                "model",
                Strings.authPopup.selectModal,
                filteredModels.map((m: any) => ({
                  label: m.name,
                  value: m.name,
                })),

                240,
              )}
            </>
          )}
        </Stack>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          justifyContent="flex-end"
          sx={{ pt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={() => onClose()}
            sx={{
              px: isMobile ? 2 : 6,
              py: 1.8,
              width: isMobile ? "100%" : "auto",
            }}
          >
            {Strings.button.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleBuyButton}
            sx={{
              px: isMobile ? 2 : 6,
              py: 1.8,
              width: isMobile ? "100%" : "auto",
            }}
          >
            {Strings.button.continue}
          </LoadingButton>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export { BuyCarForm };
